import React, { useEffect, useMemo, useState } from "react";
import { useGame, EM2C_RESOURCES } from "../game/em2c-store";
import { installDebugGodModeIntegration } from "../game/systems/DebugGodModeIntegration";
import { sfxClick } from "../game/audio";
import { loadShowcaseColony, hasShowcaseBackup, restoreBeforeShowcase } from "../game/scenarios/showcaseColony";
import {
  Bug, Zap, Droplet, Wind, Wheat, Layers, Cog, Package, Coins, CloudFog,
  Users, FlaskConical, RotateCw, X, ScanLine, ShieldCheck, Gauge,
} from "lucide-react";

installDebugGodModeIntegration();

const SPEEDS = [1, 3, 8, 20];
const HIDDEN_RESOURCE_KEYS = new Set(["ice"]);
const RESOURCE_PRIORITY = ["power", "water", "oxygen", "food", "raw_water", "potable_water", "metal_ore", "sorted_ore", "refined_metal", "machine_parts", "polymers", "credits"];
const ICONS = {
  power: Zap, water: Droplet, raw_water: Droplet, potable_water: Droplet,
  oxygen: Wind, food: Wheat, raw_food: Wheat, processed_food: Wheat,
  refined_metal: Layers, metal_ore: Layers, sorted_ore: Layers, raw_ore: Layers,
  machine_parts: Cog, credits: Coins,
};

function humanize(key) {
  return String(key || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function getResourceName(key) {
  if (key === "water") return "Clean Water";
  if (key === "potable_water") return "Clean Water (Legacy)";
  return EM2C_RESOURCES[key]?.label || humanize(key);
}

export default function DebugPanel() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const close = () => setIsOpen(false);
    window.addEventListener("mars:world-click", close);
    return () => window.removeEventListener("mars:world-click", close);
  }, []);

  const resourceKeySignature = useGame((s) => Object.keys(s.resources || {}).sort().join("|"));
  const godMode = useGame((s) => Boolean(s.godMode));
  const setGodMode = useGame((s) => s.setGodMode);
  const devMaxResource = useGame((s) => s.devMaxResource);
  const devMaxAllResources = useGame((s) => s.devMaxAllResources);
  const devTriggerStorm = useGame((s) => s.devTriggerStorm);
  const devSpawnColonists = useGame((s) => s.devSpawnColonists);
  const devCompleteAllResearch = useGame((s) => s.devCompleteAllResearch);
  const setSpeed = useGame((s) => s.setSpeed);
  const gameSpeed = useGame((s) => s.gameSpeed);
  const reset = useGame((s) => s.reset);
  const discoverDeposits = useGame((s) => s.discoverDeposits);

  const resourceKeys = useMemo(() => {
    const priority = new Map(RESOURCE_PRIORITY.map((key, index) => [key, index]));
    return resourceKeySignature.split("|").filter(Boolean).filter((key) => !HIDDEN_RESOURCE_KEYS.has(key)).sort((a, b) => {
      const pa = priority.has(a) ? priority.get(a) : 999;
      const pb = priority.has(b) ? priority.get(b) : 999;
      if (pa !== pb) return pa - pb;
      return getResourceName(a).localeCompare(getResourceName(b));
    });
  }, [resourceKeySignature]);

  const handleToggle = () => { try { sfxClick(); } catch (_) {} setIsOpen((prev) => !prev); };
  const handleGodMode = () => { try { sfxClick(); } catch (_) {} setGodMode?.(!godMode); };
  const handleMaxResource = (key) => { try { sfxClick(); } catch (_) {} devMaxResource?.(key); };
  const handleMaxAllResources = () => { try { sfxClick(); } catch (_) {} devMaxAllResources?.(); };
  const handleTriggerStorm = () => { try { sfxClick(); } catch (_) {} devTriggerStorm?.(); };
  const handleSpawnColonists = () => { try { sfxClick(); } catch (_) {} devSpawnColonists?.(); };
  const handleCompleteResearch = () => { try { sfxClick(); } catch (_) {} devCompleteAllResearch?.(); };
  const handleSetSpeed = (speed) => { try { sfxClick(); } catch (_) {} setSpeed?.(speed); };
  const handleReset = () => { try { sfxClick(); } catch (_) {} reset?.(); };
  const [backupAvailable, setBackupAvailable] = useState(() => hasShowcaseBackup());
  const handleRestorePrevious = () => {
    if (!window.confirm("Restore the colony saved before the showcase? The current showcase save will be replaced.")) return;
    try { restoreBeforeShowcase(); setIsOpen(false); window.alert("Your previous colony was restored."); }
    catch (error) { window.alert(`Restore failed: ${error?.message || error}`); }
  };
  const handleShowcase = () => {
    if (!window.confirm("Load the complete 53-type showcase colony? Your current local colony will be backed up, then replaced by the preset. The preset starts paused.")) return;
    try {
      const report = loadShowcaseColony();
      setBackupAvailable(hasShowcaseBackup());
      window.alert(`Loaded ${report.total} colony modules covering all ${report.unique} building types. Unpause to start simulation.`);
      setIsOpen(false);
    } catch (error) {
      window.alert(`Showcase could not load: ${error?.message || error}`);
    }
  };
  const handleRevealDeposits = () => { try { sfxClick(); } catch (_) {} discoverDeposits?.(); };

  if (!isOpen) {
    return (
      <div className="absolute bottom-3 right-3 z-30 pointer-events-auto">
        <button data-testid="btn-toggle-debug" onClick={handleToggle} className="flex items-center gap-2 px-3 py-1.5 bg-[#0A0C10]/90 backdrop-blur-md border border-white/10 text-xs font-mono tracking-wider uppercase text-[#00e5ff] hover:bg-[#00e5ff]/20 hover:border-[#00e5ff] transition-colors rounded-none shadow-lg cursor-pointer" style={{ fontFamily: "Rajdhani, sans-serif" }}>
          <Bug size={14} className="text-[#00e5ff]" /><span>DEBUG</span>
        </button>
      </div>
    );
  }

  return (
    <div className="absolute bottom-3 right-3 z-30 pointer-events-auto w-64 bg-[#0A0C10]/90 backdrop-blur-md border border-white/10 rounded-none shadow-2xl flex flex-col" style={{ fontFamily: "Rajdhani, sans-serif" }} data-testid="debug-panel">
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 bg-white/5 shrink-0">
        <div className="flex items-center gap-2"><Bug size={16} className="text-[#00e5ff]" /><span className="text-xs font-bold font-mono tracking-widest text-[#00e5ff] uppercase">DEBUG PANEL</span></div>
        <button data-testid="btn-toggle-debug" onClick={handleToggle} className="p-1 text-slate-400 hover:text-white hover:bg-white/10 transition-colors rounded-none cursor-pointer" title="Close Debug Panel"><X size={16} /></button>
      </div>

      <div className="p-3 space-y-4 overflow-y-auto" style={{ maxHeight: "72vh" }}>
        <div>
          <div className="text-[11px] font-mono tracking-widest text-[#00e5ff] uppercase mb-1.5">Game Speed</div>
          <div className="grid grid-cols-4 gap-1">
            {SPEEDS.map((speed) => {
              const isActive = gameSpeed === speed;
              return <button key={speed} data-testid={`btn-speed-${speed}`} onClick={() => handleSetSpeed(speed)} className={`py-1 text-xs font-mono border rounded-none transition-colors cursor-pointer ${isActive ? "border-[#00e5ff] bg-[#00e5ff]/20 text-[#00e5ff] font-bold" : "border-white/20 bg-white/5 text-slate-300 hover:bg-white/10 hover:border-white/40"}`}>{speed}x</button>;
            })}
          </div>
        </div>

        <div>
          <div className="text-[11px] font-mono tracking-widest text-[#00e5ff] uppercase mb-1.5">Failure Protection</div>
          <button data-testid="btn-god-mode" onClick={handleGodMode} className={`w-full flex items-center justify-center gap-2 px-2 py-2 border text-xs font-mono font-bold rounded-none transition-colors cursor-pointer ${godMode ? "bg-[#00fa9a]/20 border-[#00fa9a] text-[#00fa9a]" : "bg-white/5 border-white/20 text-slate-300 hover:border-[#00fa9a]/60 hover:text-[#00fa9a]"}`} title="When enabled, defeat is cancelled, life support refills to current storage capacity, and colonists are revived">
            <ShieldCheck size={15} /><span>GOD MODE: {godMode ? "ON" : "OFF"}</span>
          </button>
          <div className="mt-1 text-[9px] font-mono text-slate-500 leading-tight">Defeat blocked · life support auto-maxed on failure · no restart</div>
        </div>

        <div>
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <div className="text-[11px] font-mono tracking-widest text-[#00e5ff] uppercase">Max Resources</div>
            <button data-testid="btn-max-all-resources" onClick={handleMaxAllResources} className="flex items-center gap-1 px-1.5 py-0.5 bg-[#00fa9a]/10 border border-[#00fa9a]/40 hover:bg-[#00fa9a]/25 hover:border-[#00fa9a] text-[#00fa9a] text-[10px] font-mono rounded-none transition-colors cursor-pointer" title="Fill every resource to its current colony storage limit"><Gauge size={11} />MAX ALL</button>
          </div>
          <div className="text-[9px] font-mono text-slate-500 mb-1.5">Uses current storage capacity built in this colony</div>
          <div className="grid grid-cols-2 gap-1.5">
            {resourceKeys.map((key) => {
              const name = getResourceName(key);
              const Icon = ICONS[key] || Package;
              return <button key={key} data-testid={`btn-max-${key}`} onClick={() => handleMaxResource(key)} className="flex items-center gap-1.5 px-2 py-1 bg-[#00fa9a]/10 border border-[#00fa9a]/40 hover:bg-[#00fa9a]/25 hover:border-[#00fa9a] text-[#00fa9a] text-xs font-mono rounded-none transition-colors cursor-pointer truncate" title={`Fill ${name} to its current colony storage limit`}><Icon size={14} className="shrink-0 text-[#00fa9a]" /><span className="truncate">MAX {name}</span></button>;
            })}
          </div>
        </div>

        <div>
          <div className="text-[11px] font-mono tracking-widest text-[#00e5ff] uppercase mb-1.5">Cheats & Actions</div>
          <div className="space-y-1.5">
            <button data-testid="btn-spawn-colonists" onClick={handleSpawnColonists} className="w-full flex items-center justify-center gap-2 px-2 py-1.5 bg-[#00fa9a]/10 border border-[#00fa9a]/40 hover:bg-[#00fa9a]/25 hover:border-[#00fa9a] text-[#00fa9a] text-xs font-mono rounded-none transition-colors cursor-pointer"><Users size={14} className="shrink-0" /><span>Spawn 4 Colonists</span></button>
            <button data-testid="btn-complete-research" onClick={handleCompleteResearch} className="w-full flex items-center justify-center gap-2 px-2 py-1.5 bg-[#00fa9a]/10 border border-[#00fa9a]/40 hover:bg-[#00fa9a]/25 hover:border-[#00fa9a] text-[#00fa9a] text-xs font-mono rounded-none transition-colors cursor-pointer"><FlaskConical size={14} className="shrink-0" /><span>Complete All Research</span></button>
            <button data-testid="btn-trigger-storm" onClick={handleTriggerStorm} className="w-full flex items-center justify-center gap-2 px-2 py-1.5 bg-[#ffc107]/10 border border-[#ffc107]/40 hover:bg-[#ffc107]/25 hover:border-[#ffc107] text-[#ffc107] text-xs font-mono rounded-none transition-colors cursor-pointer"><CloudFog size={14} className="shrink-0" /><span>Trigger Dust Storm</span></button>
            <button data-testid="btn-reveal-deposits" onClick={handleRevealDeposits} className="w-full flex items-center justify-center gap-2 px-2 py-1.5 bg-[#00e5ff]/10 border border-[#00e5ff]/40 hover:bg-[#00e5ff]/25 hover:border-[#00e5ff] text-[#00e5ff] text-xs font-mono rounded-none transition-colors cursor-pointer"><ScanLine size={14} className="shrink-0" /><span>Reveal All Deposits</span></button>
          </div>
        </div>

        <div>
          <div className="text-[11px] font-mono tracking-widest text-[#00e5ff] uppercase mb-1.5">Showcase Colony</div>
          <button data-testid="btn-load-showcase" onClick={handleShowcase} className="w-full px-2 py-2 bg-[#00e5ff]/10 border border-[#00e5ff]/50 hover:bg-[#00e5ff]/20 text-[#00e5ff] font-mono text-xs font-bold">LOAD ALL 53 BUILDING TYPES</button>
          <p className="text-[9px] text-slate-500 mt-1">Creates a separate full-colony preset, preserves hex rules and backs up your current local save. Starts paused.</p>
          {backupAvailable && <button data-testid="btn-restore-pre-showcase" onClick={handleRestorePrevious} className="w-full mt-2 px-2 py-2 bg-white/5 border border-white/20 text-slate-300 font-mono text-[10px] hover:bg-white/10">RESTORE PREVIOUS COLONY</button>}
        </div>
        <div>
          <div className="text-[11px] font-mono tracking-widest text-[#ff3366] uppercase mb-1.5">Reset Game</div>
          <button data-testid="btn-reset-game" onClick={handleReset} className="w-full flex items-center justify-center gap-2 px-2 py-1.5 bg-[#ff3366]/10 border border-[#ff3366]/40 hover:bg-[#ff3366]/25 hover:border-[#ff3366] text-[#ff3366] text-xs font-mono rounded-none transition-colors cursor-pointer"><RotateCw size={14} className="shrink-0" /><span>Reset Game State</span></button>
        </div>
      </div>
    </div>
  );
}
